import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { useUser } from '../auth/AuthProvider';

export interface AdminUserRow {
  uid: string;
  email: string;
  displayName: string;
  firstName?: string;
  lastName?: string;
  role: 'admin' | 'user';
  membership: {
    tier: 'trial' | 'free' | 'pro' | 'enterprise' | 'ultimate';
    status: 'active' | 'expired' | 'cancelled' | 'paused';
    validFrom?: { toDate?: () => Date; _seconds?: number; seconds?: number } | null;
    validUntil?: { toDate?: () => Date; _seconds?: number; seconds?: number } | null;
    notes?: string;
    planId?: string | null;
  } | null;
  createdAt?: unknown;
  lastSignInAt?: unknown;
  authLastSignIn?: string | null;
  disabled?: boolean;
}

const QK = ['admin-users'] as const;

export function useAdminUsers() {
  const { isAdmin } = useUser();
  return useQuery<AdminUserRow[]>({
    queryKey: QK,
    enabled: isAdmin,
    staleTime: 15_000,
    queryFn: async () => {
      const fn = httpsCallable<unknown, { items: AdminUserRow[] }>(functions, 'listAllUsers');
      const r = await fn({});
      return r.data.items;
    },
  });
}

export function useUpdateMembership() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      uid: string;
      tier?: AdminUserRow['membership'] extends infer M ? (M extends { tier: infer T } ? T : never) : never;
      status?: AdminUserRow['membership'] extends infer M ? (M extends { status: infer S } ? S : never) : never;
      validFromMs?: number;
      validUntilMs?: number | null;
      notes?: string;
      planId?: string | null;
    }) => {
      const fn = httpsCallable<typeof input, { ok: true }>(functions, 'updateMembership');
      const r = await fn(input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
  });
}

export function useSetUserRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { uid: string; role: 'admin' | 'user' }) => {
      const fn = httpsCallable<typeof input, { ok: true }>(functions, 'setUserRole');
      const r = await fn(input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
  });
}

export function useSetUserDisabled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { uid: string; disabled: boolean }) => {
      const fn = httpsCallable<typeof input, { ok: true }>(functions, 'setUserDisabled');
      const r = await fn(input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
  });
}

export function useAdminSetUserProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      uid: string;
      firstName?: string;
      lastName?: string;
      displayName?: string;
    }) => {
      const fn = httpsCallable<typeof input, { ok: true; changed: number }>(
        functions,
        'adminSetUserProfile',
      );
      const r = await fn(input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
  });
}

export interface AdminVerificationDoc {
  type: 'passport' | 'companyCert' | 'utilityBill';
  fileName: string | null;
  fileType: string | null;
  sizeBytes: number | null;
  uploadedAt: { _seconds?: number; seconds?: number; toDate?: () => Date } | null;
  documentDate: { _seconds?: number; seconds?: number; toDate?: () => Date } | null;
  signedUrl: string | null;
}

export function useAdminVerificationDocs(uid: string | null) {
  return useQuery<AdminVerificationDoc[]>({
    queryKey: ['admin-verification-docs', uid] as const,
    enabled: !!uid,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const fn = httpsCallable<{ uid: string }, { items: AdminVerificationDoc[] }>(
        functions,
        'adminGetVerificationDocs',
      );
      const r = await fn({ uid: uid! });
      return r.data.items;
    },
  });
}

export interface CreateAdminUserInput {
  email: string;
  password?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  role?: 'admin' | 'user';
  membership?: {
    tier?: 'trial' | 'free' | 'pro' | 'enterprise' | 'ultimate';
    status?: 'active' | 'paused' | 'expired' | 'cancelled';
    validFromMs?: number | null;
    validUntilMs?: number | null;
    notes?: string | null;
    planId?: string | null;
  } | null;
  generateInviteLink?: boolean;
}

export interface CreateAdminUserResult {
  ok: true;
  uid: string;
  inviteLink: string | null;
}

export function useCreateAdminUser() {
  const qc = useQueryClient();
  return useMutation<CreateAdminUserResult, Error, CreateAdminUserInput>({
    mutationFn: async (input) => {
      const fn = httpsCallable<CreateAdminUserInput, CreateAdminUserResult>(
        functions,
        'adminCreateUser',
      );
      const r = await fn(input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
  });
}

export function useDeleteAdminUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (uid: string) => {
      const fn = httpsCallable<{ uid: string }, { ok: true }>(functions, 'deleteAdminUser');
      const r = await fn({ uid });
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
  });
}

export function tsToDate(v: unknown): Date | null {
  if (!v) return null;
  // Firestore SDK Timestamp instance from snapshot subscriptions.
  const obj = v as { toDate?: () => Date; _seconds?: number; seconds?: number };
  if (obj.toDate) return obj.toDate();
  // Firebase callable serializes timestamps as { _seconds, _nanoseconds }.
  const seconds = obj._seconds ?? obj.seconds;
  if (typeof seconds === 'number') return new Date(seconds * 1000);
  if (typeof v === 'string') return new Date(v);
  return null;
}
